import { useState, useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Filter, Grid3X3, Grid2X2, SlidersHorizontal, X, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Checkbox } from '@/components/ui/checkbox';
import { Slider } from '@/components/ui/slider';
import { useCurrency } from '@/context/CurrencyContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { slug: 'all', name: 'All Products', description: 'Explore our complete collection of handcrafted luxury textiles.' },
  { slug: 'apparel', name: 'Apparel', description: 'Exquisite garments woven with tradition and modern elegance.' },
  { slug: 'accessories', name: 'Accessories', description: 'Scarves, stoles, and wraps that elevate every ensemble.' },
  { slug: 'home-living', name: 'Home & Living', description: 'Bring warmth and artistry into your living spaces.' },
  { slug: 'limited-editions', name: 'Limited Editions', description: 'Exclusive pieces, numbered and signed by master artisans.' },
];

const SORT_OPTIONS = [
  { value: 'created_at-desc', label: 'Newest' },
  { value: 'name-asc', label: 'A to Z' },
  { value: 'name-desc', label: 'Z to A' },
  { value: 'price-asc', label: 'Price: Low to High' },
  { value: 'price-desc', label: 'Price: High to Low' },
];

const COLORS = ['White', 'Beige', 'Rose', 'Blue', 'Green', 'Gold', 'Black', 'Multi'];
const MATERIALS = ['Silk', 'Cashmere', 'Wool', 'Cotton', 'Pashmina', 'Blend'];
const CRAFTS = ['Handwoven', 'Embroidered', 'Block Print', 'Ikat', 'Jamdani', 'Kantha'];

const ShopPage = () => {
  const { category } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalProducts, setTotalProducts] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [gridCols, setGridCols] = useState(4);
  const [filters, setFilters] = useState({
    colors: [],
    materials: [],
    crafts: [],
    priceRange: [0, 50000]
  });
  const [sort, setSort] = useState('created_at-desc');
  const { formatPrice, currency } = useCurrency();

  const currentCategory = CATEGORIES.find(c => c.slug === category) || CATEGORIES[0];

  useEffect(() => {
    fetchProducts();
  }, [category, currentPage, sort, filters, currency, searchParams]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const [sortField, sortOrder] = sort.split('-');
      const params = {
        page: currentPage,
        limit: 24,
        sort: sortField,
        order: sortOrder,
        currency,
        ...(category && category !== 'all' && { category: category }),
        ...(filters.colors.length && { color: filters.colors.join(',') }),
        ...(filters.materials.length && { material: filters.materials.join(',') }),
        ...(filters.crafts.length && { craft: filters.crafts.join(',') }),
        ...(searchParams.get('trending') && { is_trending: true }),
        ...(searchParams.get('featured') && { is_featured: true }),
        ...(searchParams.get('search') && { search: searchParams.get('search') }),
      };

      const response = await axios.get(`${API}/products`, { params });
      setProducts(response.data.products || []);
      setTotalProducts(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleFilter = (type, value) => {
    setFilters(prev => ({
      ...prev,
      [type]: prev[type].includes(value)
        ? prev[type].filter(v => v !== value)
        : [...prev[type], value]
    }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({ colors: [], materials: [], crafts: [], priceRange: [0, 50000] });
    setCurrentPage(1);
  };

  const hasActiveFilters = filters.colors.length || filters.materials.length || filters.crafts.length;

  return (
    <div className="pt-32 pb-24" data-testid="shop-page">
      {/* Hero */}
      <section className="chaleur-container mb-16">
        <div className="max-w-3xl">
          <p className="caption text-primary mb-4">Shop</p>
          <h1 className="text-4xl md:text-5xl font-light mb-4">{currentCategory.name}</h1>
          <p className="text-muted-foreground">{currentCategory.description}</p>
        </div>
      </section>

      {/* Category Navigation */}
      <section className="chaleur-container mb-8">
        <div className="flex gap-4 overflow-x-auto no-scrollbar pb-4">
          {CATEGORIES.map(cat => (
            <Link
              key={cat.slug}
              to={cat.slug === 'all' ? '/shop' : `/shop/${cat.slug}`}
              className={`whitespace-nowrap px-4 py-2 text-sm border transition-colors ${
                (category === cat.slug || (!category && cat.slug === 'all'))
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border hover:border-primary'
              }`}
            >
              {cat.name}
            </Link>
          ))}
        </div>
      </section>

      {/* Toolbar */}
      <section className="chaleur-container mb-8">
        <div className="flex flex-wrap gap-4 justify-between items-center">
          <div className="flex items-center gap-4">
            {/* Mobile Filter */}
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" className="lg:hidden rounded-none">
                  <SlidersHorizontal className="w-4 h-4 mr-2" /> Filters
                  {hasActiveFilters && <span className="ml-2 w-5 h-5 bg-primary text-primary-foreground text-xs flex items-center justify-center">{filters.colors.length + filters.materials.length + filters.crafts.length}</span>}
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-full max-w-sm rounded-none">
                <SheetHeader>
                  <SheetTitle>Filters</SheetTitle>
                </SheetHeader>
                <FilterPanel filters={filters} toggleFilter={toggleFilter} clearFilters={clearFilters} />
              </SheetContent>
            </Sheet>

            <span className="text-sm text-muted-foreground hidden md:block">
              {totalProducts} products
            </span>
          </div>

          <div className="flex items-center gap-4">
            {/* Sort */}
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-[180px] rounded-none">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                {SORT_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Grid Toggle */}
            <div className="hidden lg:flex items-center gap-2 border-l border-border pl-4">
              <button 
                onClick={() => setGridCols(3)}
                className={`p-2 ${gridCols === 3 ? 'text-primary' : 'text-muted-foreground'}`}
                aria-label="3 columns"
              >
                <Grid3X3 className="w-5 h-5" />
              </button>
              <button 
                onClick={() => setGridCols(4)}
                className={`p-2 ${gridCols === 4 ? 'text-primary' : 'text-muted-foreground'}`}
                aria-label="4 columns"
              >
                <Grid2X2 className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="chaleur-container">
        <div className="flex gap-8">
          {/* Desktop Sidebar Filters */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <FilterPanel filters={filters} toggleFilter={toggleFilter} clearFilters={clearFilters} />
          </aside>

          {/* Products Grid */}
          <div className="flex-1">
            {loading ? (
              <div className={`grid grid-cols-2 lg:grid-cols-${gridCols} gap-6 md:gap-8`}>
                {[...Array(8)].map((_, idx) => (
                  <div key={idx} className="animate-pulse">
                    <div className="aspect-[3/4] bg-secondary mb-4" />
                    <div className="h-4 bg-secondary rounded w-3/4 mb-2" />
                    <div className="h-4 bg-secondary rounded w-1/2" />
                  </div>
                ))}
              </div>
            ) : products.length > 0 ? (
              <>
                <div className={`grid grid-cols-2 lg:grid-cols-${gridCols} gap-6 md:gap-8`}>
                  {products.map((product, idx) => (
                    <ProductCard key={product.id} product={product} formatPrice={formatPrice} index={idx} />
                  ))}
                </div>

                {/* Pagination */}
                {totalProducts > 24 && (
                  <div className="flex justify-center gap-2 mt-12">
                    {[...Array(Math.ceil(totalProducts / 24))].map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => setCurrentPage(idx + 1)}
                        className={`w-10 h-10 flex items-center justify-center text-sm ${
                          currentPage === idx + 1 
                            ? 'bg-primary text-primary-foreground' 
                            : 'border border-border hover:border-primary'
                        }`}
                      >
                        {idx + 1}
                      </button>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-16">
                <p className="text-muted-foreground mb-4">No products found matching your criteria.</p>
                <Button onClick={clearFilters} variant="outline">Clear Filters</Button>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

// Filter Panel Component
const FilterPanel = ({ filters, toggleFilter, clearFilters }) => {
  return (
    <div className="space-y-8">
      {/* Active Filters */}
      {(filters.colors.length || filters.materials.length || filters.crafts.length) > 0 && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <span className="text-sm font-medium">Active Filters</span>
            <button onClick={clearFilters} className="text-xs text-primary hover:underline">
              Clear All
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {[...filters.colors, ...filters.materials, ...filters.crafts].map(f => (
              <span key={f} className="inline-flex items-center gap-1 px-2 py-1 bg-secondary text-xs">
                {f}
                <X className="w-3 h-3 cursor-pointer" onClick={() => {
                  if (filters.colors.includes(f)) toggleFilter('colors', f);
                  else if (filters.materials.includes(f)) toggleFilter('materials', f);
                  else toggleFilter('crafts', f);
                }} />
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Color */}
      <div>
        <h4 className="text-sm font-medium mb-4">Color</h4>
        <div className="space-y-3">
          {COLORS.map(color => (
            <label key={color} className="flex items-center gap-3 cursor-pointer">
              <Checkbox
                checked={filters.colors.includes(color)}
                onCheckedChange={() => toggleFilter('colors', color)}
                className="rounded-none"
              />
              <span className="text-sm">{color}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Material */}
      <div>
        <h4 className="text-sm font-medium mb-4">Material</h4>
        <div className="space-y-3">
          {MATERIALS.map(material => (
            <label key={material} className="flex items-center gap-3 cursor-pointer">
              <Checkbox
                checked={filters.materials.includes(material)}
                onCheckedChange={() => toggleFilter('materials', material)}
                className="rounded-none"
              />
              <span className="text-sm">{material}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Craft Technique */}
      <div>
        <h4 className="text-sm font-medium mb-4">Craft Technique</h4>
        <div className="space-y-3">
          {CRAFTS.map(craft => (
            <label key={craft} className="flex items-center gap-3 cursor-pointer">
              <Checkbox
                checked={filters.crafts.includes(craft)}
                onCheckedChange={() => toggleFilter('crafts', craft)}
                className="rounded-none"
              />
              <span className="text-sm">{craft}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
};

// Product Card Component
const ProductCard = ({ product, formatPrice, index }) => {
  const variant = product.variants?.[0];
  const price = variant?.price || 0;
  const comparePrice = variant?.compare_at_price;
  const imageUrl = product.images?.[0]?.startsWith('/') 
    ? product.images[0] 
    : (product.images?.[0] || '/images/products/IMG_6195.jpg');
  
  // Calculate points - simply 1% of displayed price
  const pointsEarned = Math.floor(price * 0.01);

  return (
    <Link 
      to={`/product/${product.slug}`} 
      className="product-card animate-fade-in-up"
      style={{ animationDelay: `${index * 0.05}s` }}
      data-testid={`product-card-${product.id}`}
    >
      <div className="product-card-image">
        <img 
          src={imageUrl}
          alt={product.name}
          loading="lazy"
        />
        {product.is_limited_edition && (
          <span className="absolute top-4 left-4 bg-foreground text-background caption px-3 py-1">
            Limited Edition
          </span>
        )}
        {product.is_trending && !product.is_limited_edition && (
          <span className="absolute top-4 left-4 bg-primary text-primary-foreground caption px-3 py-1">
            Trending
          </span>
        )}
      </div>
      <div className="mt-4 space-y-1">
        <h3 className="text-sm font-medium line-clamp-1">{product.name}</h3>
        <p className="text-xs text-muted-foreground">{product.craft_technique || 'Handcrafted'}</p>
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">{formatPrice(price)}</span>
          {comparePrice && (
            <span className="text-xs text-muted-foreground line-through">{formatPrice(comparePrice)}</span>
          )}
        </div>
        {pointsEarned > 0 && (
          <p className="text-xs text-amber-600 font-medium">
            Earn {pointsEarned} points
          </p>
        )}
      </div>
    </Link>
  );
};

export default ShopPage;
